import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEraser, faLink, faXmark } from '@fortawesome/free-solid-svg-icons';
import { AvmFormNullableDateValue } from '@hub4b/pipes/avm-form-nullable-date-value';
import { AvmFormNullableListValue } from '@hub4b/pipes/avm-form-nullable-list-value';
import { AvmFormNullableValue } from '@hub4b/pipes/avm-form-nullable-value';
import { AvmLinkPipe } from '@hub4b/pipes/avm-link.pipe';
import { NGX_MAT_DATE_FORMATS, NgxMatDateFormats, NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import moment, { Moment } from 'moment';
import { debounceTime, distinctUntilChanged, Observable, switchMap, tap } from 'rxjs';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { InstantErrorStateMatcher } from 'src/app/core/instant-error-state-matcher';
import { DateHelper } from 'src/app/helpers/date-helper';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { MomentHelper } from 'src/app/helpers/moment-helper';
import { PropertyType } from 'src/app/models/enums/property-type';
import { ListItem } from 'src/app/models/list-item';
import { RestApiConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-configuration';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { Colors } from 'src/app/models/server/enums/colors';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { AutocompletionService } from 'src/app/services/http/autocompletion.service';
import { RestApiConfigurationsService } from 'src/app/services/http/rest-api-configurations.service';

export const ATTRIBUTE_DATE_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

export const ATTRIBUTE_DATETIME_FORMATS: NgxMatDateFormats = {
    parse: {
        dateInput: 'dd/MM/YYYY HH:mm',
    },
    display: {
        dateInput: 'dd/MM/YYYY HH:mm',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'dd/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-attribute',
    templateUrl: './attribute.component.html',
    styleUrls: ['./attribute.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: ATTRIBUTE_DATE_FORMATS },
        { provide: NGX_MAT_DATE_FORMATS, useValue: ATTRIBUTE_DATETIME_FORMATS },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
    ],
    imports: [
        NgClass,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatOptionModule,
        MatDatepickerModule,
        MatButtonModule,
        FaIconComponent,
        AvmLinkPipe,
        AvmFormNullableValue,
        AvmFormNullableDateValue,
        AvmFormNullableListValue,
        MatMenuModule,
        MatAutocompleteModule,
        CommonModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
    ],
})
export class AttributeComponent extends BaseDirective implements OnInit {
    /**
     * Références des icônes utilisés dans le template.
     */
    public faLink = faLink;
    public faxmark = faXmark;
    public faEraser = faEraser;

    /**
     * Références des Enums utilisées dans le template.
     */
    public PropertyType = PropertyType;
    public Colors = Colors;

    /**
     * Gestionnaire des messages d'erreur.
     */
    public errorStateMatcher = new InstantErrorStateMatcher();

    /**
     * Définition de l'attribut affiché.
     */
    private _attributeDefinition: AttributeDefinition;

    /**
     * Spécifie la définition de l'attribut affiché.
     */
    @Input()
    public set attributeDefinition(value: AttributeDefinition) {
        this._attributeDefinition = value;

        if (this._attributeDefinition.values) {
            this.RetrieveAttributeValues();
        }
    }

    /**
     * Obtient la définition de l'attribut affiché.
     */
    public get attributeDefinition(): AttributeDefinition {
        return this._attributeDefinition;
    }

    /**
     * Valeur de l'attribut affiché.
     */
    private _value: string;

    /**
     * Définit la valeur de l'attribut affiché.
     */
    @Input()
    set value(v: string) {
        this.SetValueIntoField(v);
    }

    /**
     * Obtient la valeur de l'atttribut affiché.
     */
    get value(): string {
        return this._value;
    }

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Emis lorsque l'attribut est mis à jour.
     */
    @Output()
    public valueUpdated: EventEmitter<string> = new EventEmitter();

    /**
     * Reference au menu contextuel
     */
    @ViewChild(MatMenuTrigger, { static: true })
    private contextMenu: MatMenuTrigger;

    /**
     * Position du menu contextuel.
     */
    public contextMenuPosition = { x: '0px', y: '0px' };

    /**
     * Eléments affichés dans la liste déroulante dans le cas d'un attribut de type liste.
     */
    public listItems: ListItem[];

    /**
     * Valeur indiquant que l'attribut n'est pas renseigné alors qu'il est obligatoire.
     */
    public isRequiredError: boolean = false;

    /**
     * Valeur indiquant que l'attribut est invalide.
     * La valeur de l'attribut est invalide si elle ne respecte pas l'expression régulière correspond au format définit dans la définition de l'attribut).
     */
    public isInvalidError: boolean = false;

    /**
     * Message explicitant pourquoi la valeur saisie est invalide.
     */
    public invalidMessage: string = '';

    /**
     * Dans le cas d'un attribut de type Couleur, il s'agit de la couleur correspondant à la valeur de l'attribut.
     */
    public selectedColor: Colors = null;

    /**
     * Dans le cas d'un attribut de type Liste, il s'agit de la clé de l'élément de la liste selectionné.
     */
    public selectedListItemKey: string = null;

    /**
     * Dans le cas d'un attribut de type Date, il s'agit de la date correspondant à la valeur de l'attribut.
     */
    public dateValue: Date = null;

    /**
     * Contrôle de formulaire associé à l'attribut.
     */
    public attributeFormControl = new UntypedFormControl(null);

    /**
     * Expression régulière que doit respecter la valeur de l'attribut (correspond au format définit dans la définition de l'attribut).
     */
    private pattern: RegExp;

    /**
     * Liste des valeurs proposées par l'autocomplétion.
     * (Utilisé uniquement par le type d'attribut 'Autocompletion par API')
     */
    public autocompletionValues: Observable<string[]>;

    /**
     * Instance de la configuration d'API.
     * (Utilisé uniquement par le type d'attribut 'Autocompletion par API')
     */
    public restApiConfiguration: RestApiConfiguration = null;

    /**
     * Valeur selectionnée dans le cas d'un attribut de type 'Autocompletion par API'.
     */
    private autoCompletionSelectedValue: string = '';

    /**
     * Constructeur.
     * @param enumHelper
     * @param dialogService
     * @param dateHelper
     */
    constructor(
        public enumHelper: EnumHelper,
        private dialogService: DialogService,
        private dateHelper: DateHelper,
        private autocompletionService: AutocompletionService,
        private restApiConfigurationsService: RestApiConfigurationsService
    ) {
        super();
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.applyValidators();

        if (!this.readonly && this.attributeDefinition.type === PropertyType.AutocompletionByApi) {
            // this.filteredOptions = this.attributeFormControl.valueChanges.pipe(
            //     startWith(''),

            //     debounceTime(400),

            //     distinctUntilChanged(),

            //     switchMap((val) => {
            //         return this.filter(val || '');
            //     })
            // );
            // Chargement de la configuration d'API
            this.restApiConfigurationsService.get(this.attributeDefinition.configurationApiId).subscribe((x) => {
                this.restApiConfiguration = x;
            });

            this.autocompletionValues = this.attributeFormControl.valueChanges.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                tap((value) => console.log('Autocomplete : ', value)),
                switchMap((val) => {
                    return this.filter(val || '');
                })
            );
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton d'edition d'un lien.
     * @param value : valeur du lien.
     */
    public onEditLink(value: string): void {
        this.dialogService.openEditLinkAttribute(value).subscribe((x) => {
            if (x) {
                this.onAttributeInputChange(x);
            }
        });
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton de suppression d'un lien.
     */
    public onDeleteLink(): void {
        this.onAttributeInputChange(null);
    }

    /**
     * Appelée lorsque la valeur d'un attribut de type date ou date & heure change (selection d'une date, selection d'une date et d'une heure ...)
     * @param $event Événement émis par le champ date.
     */
    public onAttributeDateChange($event: any): void {
        this.resetErrors();

        if (!!!$event || !!!$event.value) {
            if (this.attributeDefinition.mandatory) {
                this.isRequiredError = true;
                return;
            }
        }

        if (this.attributeDefinition.type === PropertyType.Date) {
            this.applyDateValue($event.value);
        }

        if (this.attributeDefinition.type === PropertyType.DateAndTime) {
            this.applyDateAndTimeValue($event.value);
        }
    }

    /**
     * Appelée lorsque le champ de saisie est modifié (perte de focus, selection dans une liste)
     * @param inputText Événement.
     */
    public onAttributeInputChange(inputText): void {
        let value: string = null;

        this.isRequiredError = false;
        this.isInvalidError = false;

        if (!!!inputText || inputText === '') {
            if (this.attributeDefinition.mandatory) {
                this.isRequiredError = true;
                this.applyValue(null);
                return;
            }
        }

        if (this.attributeDefinition.type === PropertyType.DateAndTime) {
            if (inputText === null || inputText === '') {
                // Saisie vide
                value = null;
            } else {
                let m: Moment = MomentHelper.parseFrenchDateTime(inputText);

                if (m.isValid()) {
                    // Format YYYY-MM-DDTHH:mm:ssZ
                    value = this.dateHelper.formatAsIso8601DateTime(m.toDate());
                    this.applyValue(value);
                } else {
                    this.isInvalidError = true;
                    this.invalidMessage = "La date ou l'heure n'est pas valide.";
                    return;
                }
            }
        }

        if (this.attributeDefinition.type === PropertyType.Long) {
            this.applyLongValue(inputText);
        }

        if (this.attributeDefinition.type === PropertyType.Decimal) {
            this.applyDecimalValue(inputText);
        }

        if (
            this.attributeDefinition.type === PropertyType.List ||
            this.attributeDefinition.type === PropertyType.ListCustomers ||
            this.attributeDefinition.type === PropertyType.ListUsers
        ) {
            value = inputText;
            this.applyValue(value);
        }

        if (this.attributeDefinition.type === PropertyType.AutocompletionByApi) {
            value = inputText;
            this.applyValue(value);
        }

        if (this.attributeDefinition.type === PropertyType.Link) {
            value = inputText;
            this.applyValue(value);
        }

        if (
            this.attributeDefinition.type === PropertyType.Text ||
            this.attributeDefinition.type === PropertyType.Email ||
            this.attributeDefinition.type === PropertyType.PhoneNumber
        ) {
            if (inputText !== '') {
                value = inputText;
            }

            this.applyValue(value);
        }

        if (this.attributeDefinition.type === PropertyType.Color) {
            this.applyColorValue(inputText);
        }
    }

    /**
     * Appelée lorsqu'une valeur de la liste proposée par l'autocomplétion est selectionnée.
     * @param event Événement.
     */
    public onAutocompleteItemSelected(event: MatAutocompleteSelectedEvent): void {
        let value: string = event.option.value;

        this.isRequiredError = false;
        this.isInvalidError = false;

        this.autoCompletionSelectedValue = value;
        this.applyValue(value);
    }

    /**
     * Appelée lorsque la liste proposée par l'autocomplétion est fermée.
     */
    public onAutocompleteClosed(): void {
        console.log('closed');
        this.isRequiredError = false;
        this.isInvalidError = false;

        let value = this.attributeFormControl.value;

        if (this.autoCompletionSelectedValue !== value && value !== '') {
            this.attributeFormControl.setValue(this.autoCompletionSelectedValue);
        }

        if (!!!value || value === '') {
            if (this.attributeDefinition.mandatory) {
                this.isRequiredError = true;
                return;
            }
        }
    }

    /**
     * Appellée lorsque l'utilisateur colle du texte dans le champ.
     * @param event
     */
    public onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const clipboardData = event.clipboardData;
        let pastedText = clipboardData.getData('text');

        if (this.attributeDefinition.options.removeSpaceFromPastedText) {
            // Supprime tous les espaces
            pastedText = pastedText.replace(/\s+/g, '');
        } else if (this.attributeDefinition.options.trimPastedText) {
            // Supprime les espaces au début et à la fin du texte
            pastedText = pastedText.trim();
        }

        this.attributeFormControl.setValue(pastedText);
    }

    /**
     * Applique les validateurs sur le champ de formualire en fonction du type d'attribut.
     */
    private applyValidators() {
        if (!!this.attributeDefinition.format) {
            this.pattern = new RegExp(this.attributeDefinition.format);

            this.attributeFormControl.addValidators(Validators.pattern(this.attributeDefinition.format));
        }

        if (this.attributeDefinition.mandatory === true) {
            this.attributeFormControl.addValidators(Validators.required);
        }

        if (this.attributeDefinition.type === PropertyType.Long) {
            this.attributeFormControl.addValidators(IsInteger());
        }

        if (this.attributeDefinition.type === PropertyType.Decimal) {
            this.attributeFormControl.addValidators(IsDecimal());
        }
    }

    /**
     * Récupère les valeurs d'un attribut de type 'Liste'.
     */
    private RetrieveAttributeValues() {
        this.listItems = this._attributeDefinition.values;
    }

    /**
     * Renseigne le champ de formulaire avec la valeur de l'attribut.
     * @param value Valeur de l'attribut.
     */
    private SetValueIntoField(value: string) {
        this._value = value;

        if (this.attributeDefinition.type === PropertyType.Color) {
            this.SetColorValueIntoField(value);
        } else if (this.attributeDefinition.type === PropertyType.Date) {
            this.setDateValueIntoField(value);
        } else if (this.attributeDefinition.type === PropertyType.DateAndTime) {
            this.setDateAndTimeValueIntoField(value);
        } else if (
            this.attributeDefinition.type === PropertyType.List ||
            this.attributeDefinition.type === PropertyType.ListCustomers ||
            this.attributeDefinition.type === PropertyType.ListUsers
        ) {
            this.setListValueIntoField(value);
        } else if (this.attributeDefinition.type === PropertyType.AutocompletionByApi) {
            this.attributeFormControl.setValue(value);
            this.autoCompletionSelectedValue = value;
        } else {
            this.attributeFormControl.setValue(value);
        }
    }

    /**
     * Renseigne le champ de formulaire de type 'Liste' avec la valeur de l'attribut.
     * @param value Valeur de l'attribut.
     */
    private setListValueIntoField(value: string) {
        if (value) {
            this.selectedListItemKey = value;
            this.attributeFormControl.setValue(this.selectedListItemKey);
        } else {
            this.selectedListItemKey = null;
        }
    }

    /**
     * Renseigne le champ de formulaire de type 'Date & Heure' avec la valeur de l'attribut.
     * @param value Valeur de l'attribut.
     */
    private setDateAndTimeValueIntoField(value: string) {
        if (value) {
            // Format YYYY-MM-DDTHH:mm:ssZ
            let m: Moment = moment(value, moment.defaultFormat);

            this.dateValue = m.toDate();
        } else {
            this.dateValue = null;
        }

        this.attributeFormControl.setValue(this.dateValue);
    }

    /**
     * Renseigne le champ de formulaire de type 'Date' avec la valeur de l'attribut.
     * @param value Valeur de l'attribut.
     */
    private setDateValueIntoField(value: string) {
        if (value) {
            // Format YYYY-MM-DD
            let m: Moment = moment(value, moment.defaultFormat);

            this.dateValue = m.toDate();
        } else {
            this.dateValue = null;
        }

        this.attributeFormControl.setValue(this.dateValue);
    }

    /**
     * Renseigne le champ de formulaire de type 'Couleur' avec la valeur de l'attribut.
     * @param value Valeur de l'attribut.
     */
    private SetColorValueIntoField(value: string) {
        this.selectedColor = this.enumHelper.convertColor(value);
        this.attributeFormControl.setValue(this.selectedColor);
    }

    /**
     * Réinitialise les erreurs.
     */
    private resetErrors() {
        this.isRequiredError = false;
        this.isInvalidError = false;
    }

    /**
     * Applique la valeur de la date selectionnée
     * @param eventValue : Valeur de l'evenement de changement de date.
     */
    private applyDateValue(eventValue: any): void {
        let value: string;

        if (eventValue === null) {
            // Saisie vide
            this.valueUpdated.emit(null);
        } else {
            let m: Moment = eventValue;

            if (m.isValid()) {
                // Format YYYY-MM-DD
                value = this.dateHelper.formatAsIso8601Date(m.toDate());
                this.valueUpdated.emit(value);
            } else {
                this.isInvalidError = true;
                this.invalidMessage = "La date n'est pas valide.";
            }
        }
    }

    /**
     * Applique la valeur de la date & heure selectionnée
     * @param eventValue : Valeur de l'evenement de changement de date.
     */
    private applyDateAndTimeValue(eventValue): void {
        let value: string;

        if (eventValue === null) {
            // Saisie vide
            this.valueUpdated.emit(null);
        } else {
            // Selection d'une date
            let m: Moment = eventValue;

            if (m.isValid()) {
                // Format YYYY-MM-DDTHH:mm:ssZ
                value = this.dateHelper.formatAsIso8601DateTime(m.toDate());
                this.dateAndTimeValue = value;
                return;
            } else {
                this.isInvalidError = true;
                this.invalidMessage = "La date ou l'heure n'est pas valide.";
                return;
            }
        }
    }

    private applyLongValue(inputText: string) {
        let value: string = null;

        if (inputText === null || inputText === '') {
            // Saisie vide
            value = null;
        } else {
            const numberValue = Number(inputText);

            if (Number.isInteger(numberValue) === true) {
                value = inputText;
            } else {
                // Erreur gérée par le validateur
                return;
            }
        }

        this.applyValue(value);
    }

    private applyDecimalValue(inputText: string) {
        let value: string = null;

        if (inputText === null || inputText === '') {
            // Saisie vide
            value = null;
        } else {
            // Formate la valeur saisie si le séparateur décimal virgule est utilisé au lieu du spéparateur point.
            inputText = inputText.replace(',', '.');

            const numberValue = Number.parseFloat(inputText);

            if (isNaN(numberValue)) {
                // Erreur gérée par le validateur
                return;
            } else {
                value = numberValue.toString();
            }
        }

        this.applyValue(value);
    }

    private applyColorValue(inputText: string) {
        let value: string = null;

        if (inputText !== '') {
            value = inputText;
            this.selectedColor = this.enumHelper.convertColor(inputText);
        }

        this.applyValue(value);
    }

    private applyValue(value: string) {
        if (value === null) {
            // Cas d'un champ vide non obligatoire
            this.valueUpdated.emit(value);
        } else {
            if (!!this.attributeDefinition.format) {
                // Présence d'un format sur l'attribut.
                if (this.pattern.test(value)) {
                    // La valeur respecte le format.
                    this.valueUpdated.emit(value);
                } else {
                    // La valeur ne respecte pas le format.
                    this.isInvalidError = true;

                    if (this.attributeDefinition.type === PropertyType.Email) {
                        this.invalidMessage = `L'adresse e-mail n'est pas valide.`;
                    } else if (this.attributeDefinition.type === PropertyType.PhoneNumber) {
                        this.invalidMessage = `Le n° de téléphone n'est pas valide.`;
                    } else {
                        this.invalidMessage = `Le champ doit respecter l'expression régulière : ${this.attributeDefinition.format}.`;
                    }
                }
            } else {
                // L'attribut ne specifie pas de format.
                this.valueUpdated.emit(value);
            }
        }
    }

    private filter(val: string): Observable<string[]> {
        return this.autocompletionService.getDatas(this.restApiConfiguration, val.toLowerCase());
    }

    dateAndTimeValue: string;

    onClose() {
        if (this.dateAndTimeValue !== this.value) {
            this.valueUpdated.emit(this.dateAndTimeValue);
        }
    }

    onOpen() {
        this.dateAndTimeValue = this.value;
    }

    public openMenu(event: MouseEvent) {
        if (this.attributeDefinition.mandatory === false && this.readonly === false) {
            this.contextMenuPosition.x = event.clientX - 5 + 'px';
            this.contextMenuPosition.y = event.clientY - 10 + 'px';
            this.contextMenu.openMenu();
        }
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event
     * @param data
     */
    onContextMenuClick(event: MouseEvent, menuItem: string): any {
        if (menuItem === 'erase') {
            if (this.attributeDefinition.type === PropertyType.Color) {
                this.value = Colors.None.toString();
            } else {
                this.value = null;
            }
            this.valueUpdated.emit(this.value);
        }
    }
}

/**
 * Fonction permettant de valider que le la valeur d'un champ est un nombre entier.
 * @returns Une erreur si la valeur du champ n'est pas un nombre entier; null autrement.
 */
export function IsInteger(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const numberValue = Number(control.value);

        if (Number.isInteger(numberValue) === false) {
            return { invalidError: true };
        } else {
            return null;
        }
    };
}

/**
 * Fonction permettant de valider que le la valeur d'un champ est un nombre décimal.
 * @returns Une erreur si la valeur du champ n'est pas un nombre décimal; null autrement.
 */
export function IsDecimal(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const decimalPattern: RegExp = /^-?\d+(,\d+)*(\.\d+(e\d+)?)?$/;

        let inputText = control.value as string;

        if (inputText === null || inputText === '') {
            // Saisie vide
            return null;
        } else {
            // Formate la valeur saisie si le séparateur décimal virgule est utilisé au lieu du spéparateur point.
            inputText = inputText.replace(',', '.');

            const numberValue = Number.parseFloat(inputText);

            // Test si la valeur est un décimal (au sens javascript --> permet de saisir des caractères texte avant et aprés) et si elle respecte la regex decimale.
            if (isNaN(numberValue) || !decimalPattern.test(inputText)) {
                return { invalidError: true };
            } else {
                return null;
            }
        }
    };
}
